/**
 * ControllerRegistry — dispatch typé `name → constructor` (ha/440).
 *
 * Remplace deux mécanismes legacy par réflexion sur objets-namespace globaux :
 *
 *  1. `createService(namespace, name, data) = new (namespace)[name](data)`
 *     (cf. ancien `HA.createService` dans `src/HA.ts`) — instanciait un service
 *     par nom string en lisant la propriété d'un objet-namespace global.
 *  2. `Entity.resolveControllerNamespace(name)` — switch hard-codé qui mappait
 *     un controllerNamespace string vers `HA.Core.Controller.Navigation` /
 *     `Menu` / `Page`, lus comme objets globaux.
 *
 * En ESM, les objets-namespace n'existent plus → `new ns[name]` casse net. Le
 * registre fournit un dispatch explicite, vérifiable à la compilation,
 * tree-shakable et testable.
 *
 * Contrat :
 *  - `register(name, ctor)` — l'appelant (le module qui définit `ctor`)
 *    s'enregistre à l'import. Idempotent (un même name redéfini écrase, avec
 *    warning) pour supporter le HMR/tests.
 *  - `create(name, data)` — instancie `new ctor(data)` ; lève si name inconnu.
 *  - `has(name)` — predicat de présence (utile aux tests).
 *
 * Enregistrement : chaque module de contrôleur fait un side-effect
 * `ControllerRegistry.register("MyController", MyController)` au top-level.
 * Le boot importe les modules → registre peuplé avant tout `create()`.
 */

export type ControllerCtor = new (data?: unknown) => unknown;

const _registry = new Map<string, ControllerCtor>();

export const ControllerRegistry = {
    /**
     * Enregistre un constructeur sous un nom. Si le nom existe déjà, écrase
     * silencieusement — toléré pour le hot-reload et les tests qui réinjectent
     * un mock. Le code applicatif n'enregistre chaque ctor qu'une fois.
     */
    register(name: string, ctor: ControllerCtor): void {
        _registry.set(name, ctor);
    },

    /**
     * Instancie le contrôleur enregistré sous ce nom. Lève une Error explicite
     * si le nom est inconnu — diagnostic clair côté dev, mieux que le silencieux
     * `undefined.constructor` du dispatch par réflexion.
     */
    create(name: string, data?: unknown): unknown {
        const ctor = _registry.get(name);
        if (!ctor) {
            throw new Error(`ControllerRegistry: no controller registered for "${name}"`);
        }
        return new ctor(data);
    },

    has(name: string): boolean {
        return _registry.has(name);
    },

    /**
     * Vide le registre. Réservé aux tests — ne PAS appeler en runtime
     * applicatif (les enregistrements top-level ne se rejouent pas).
     */
    _clearForTests(): void {
        _registry.clear();
    },

    /** Inspection lecture seule des noms enregistrés (debug/tests). */
    _names(): readonly string[] {
        return Array.from(_registry.keys());
    },
};
